import type { CallbackDataParams } from 'echarts/types/dist/shared';

import { fieldShort } from '@/features/decisions/config/fieldLabels';
import { POLICY_IDS } from '@/features/decisions/config/personaContext';
import type { InventoryCoveragePolicy } from '@/features/decisions/decisions.api.types';
import { addDaysToIsoDate } from '@/utils/date';

const COLOR_AVAILABLE = '#1e293b'; // slate-800
const COLOR_INFLIGHT = '#94a3b8'; // slate-400
const COLOR_REORDER = '#ef4444'; // red-500
const COLOR_INV = '#047857'; // emerald-700
const COLOR_DEMAND = '#f59e0b'; // amber-500
const COLOR_MEAN = '#22d3ee'; // cyan-400
const COLOR_Q80 = '#93c5fd'; // blue-300
const COLOR_Q90 = '#172554'; // blue-950
const COLOR_Q95 = '#3b82f6'; // blue-500
const COLOR_TARGET = '#172554'; // blue-950 — target-SL line emphasis

// One quantile level of the cumulative-demand forecast: probability p in (0,1)
// and the demand value at that quantile per period.
export type ForecastQuantileLevel = { p: number; values: (number | null)[] };

export type ForecastQuantiles = {
  timestamps: string[]; // YYYY-MM-DD base date per entry from MDS response
  spans: number[];
  mean: (number | null)[];
  quantile80: (number | null)[];
  quantile90: (number | null)[];
  quantile95: (number | null)[];
  // Full quantile grid (all levels the API serves), used for stockout-risk
  // interpolation. Mirrors the backend's QuantileDemandStrategy (empirical grid),
  // rather than a parametric fit.
  grid?: ForecastQuantileLevel[];
};

const fmtUnits = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v).toLocaleString()}u`);

// Continuous weekly dates start..end inclusive, so the lead-time weeks the
// projection skips still render as buckets (shared weekly axis with the stockout
// chart, so the ①②③ markers line up).
function weeklyDates(start: string, end: string): string[] {
  const out = [start];
  let cur = start;
  let guard = 0;
  while (cur < end && guard < 104) {
    cur = addDaysToIsoDate(cur, 7);
    out.push(cur);
    guard += 1;
  }
  return out;
}

// "Inventory Coverage vs Demand" for one previewed policy: a stack at the
// decision date (on-hand + in-transit + reorder) and the projected inventory
// position over the lead-time + planning-horizon window.
export function buildInventoryCoverageChartOption(
  policy: InventoryCoveragePolicy | undefined,
  decisionDate: string,
  quantiles?: ForecastQuantiles,
) {
  if (!policy) {
    return {
      title: { text: 'No data', left: 'center', top: 'middle', textStyle: { color: '#94a3b8', fontSize: 14 } },
      xAxis: { show: false },
      yAxis: { show: false },
    };
  }

  const proj = policy.model_projected_inv_over_lt_ph ?? [];
  const demand = policy.model_expected_demand_over_lt_ph ?? [];
  const projPeriods = proj.map((p) => p.period);
  const demandPeriods = demand.map((p) => p.period);

  // Continuous weekly axis: decision date → planning horizon (last projected
  // period), filling the lead-time weeks the projection skips so the axis (and
  // the ①②③ markers) align with the weekly stockout chart. Union in the actual
  // projection/demand dates so each maps onto a bucket.
  const horizonDate = [decisionDate, ...projPeriods, ...demandPeriods].sort().slice(-1)[0] ?? decisionDate;
  const catSet = new Set<string>(weeklyDates(decisionDate, horizonDate));
  projPeriods.forEach((d) => catSet.add(d));
  demandPeriods.forEach((d) => catSet.add(d));
  const categories = Array.from(catSet).sort();
  const lastIdx = categories.length - 1;
  const pad = new Array(Math.max(0, categories.length - 1)).fill(null) as Array<number | null>;

  const available = policy.model_beginning_available ?? 0;
  const beginningInflight = policy.model_beginning_inflight ?? 0;
  const reorder = policy.model_order_constrained ?? 0;

  // Demand line mapped by date (connectNulls bridges the filled lead-time weeks).
  const demandByDate = new Map(demand.map((p) => [p.period, p.value]));
  const demandData: Array<number | null> = categories.map((c, i) => (i === 0 ? null : (demandByDate.get(c) ?? null)));

  const ending = policy.model_projected_inv;

  // ①②③ markers by date: ① decision, ② reorder available (first projected
  // period = decision + lead time), ③ next available (planning horizon).
  const receiptIdx = projPeriods.length ? categories.indexOf(projPeriods[0]) : -1;
  const horizonIdx = projPeriods.length ? categories.indexOf(projPeriods[projPeriods.length - 1]) : lastIdx;
  const markerIdx = [0, receiptIdx, horizonIdx].filter((v, i, a) => v >= 0 && v <= lastIdx && a.indexOf(v) === i);
  const markerData = markerIdx.map((idx, i) => ({ value: [idx, 0], label: { formatter: String(i + 1) } }));

  const stackTop = available + beginningInflight + reorder;
  const hasGap = ending != null && ending < 0 && reorder > 0;

  // Service-level label for the reorder annotation — driven by the policy's
  // actual target, not a hardcoded value.
  const slPct = policy.model_target_service_level != null ? Math.round(policy.model_target_service_level * 100) : null;
  const reorderGapLabel = slPct != null ? `Reorder closes the SL ${slPct}% gap` : 'Reorder closes the gap';

  // Anchor for quantile lines: top of the stacked bar (available + in-flight).
  // Each line plots anchor − cumulative_forecast so lines descend from the
  // starting inventory level as demand is consumed over the horizon.
  const anchor = available + beginningInflight;
  const applyAnchor = (v: number | null) => (v != null ? anchor - v : null);

  // Quantile lines use the category axis. Anchor at decision date (index 0)
  // so the line starts exactly at the bar top, then populate matching span dates.
  const toSparseData = (values: (number | null)[]): (number | null)[] => {
    if (!quantiles) return [];
    const sparse: (number | null)[] = categories.map(() => null);
    sparse[0] = anchor;
    quantiles.timestamps.forEach((ts, i) => {
      const idx = categories.indexOf(addDaysToIsoDate(ts, quantiles.spans[i]));
      if (idx !== -1) sparse[idx] = applyAnchor(values[i]);
    });
    return sparse;
  };

  const qMean = toSparseData(quantiles?.mean ?? []);
  const qP80 = toSparseData(quantiles?.quantile80 ?? []);
  const qP90 = toSparseData(quantiles?.quantile90 ?? []);
  const qP95 = toSparseData(quantiles?.quantile95 ?? []);

  // Target-SL quantile line: pick the grid level nearest the policy's target
  // service level (e.g. 70% → P70) so the coverage the user sees is the one the
  // policy is actually planned to. The 80/90/95 lines stay as default-off toggles.
  const targetSL = policy.model_target_service_level ?? null;
  const targetGridLevel =
    targetSL != null && quantiles?.grid?.length
      ? quantiles.grid.reduce((best, lvl) => (Math.abs(lvl.p - targetSL) < Math.abs(best.p - targetSL) ? lvl : best))
      : null;
  const targetPct = targetGridLevel ? Math.round(targetGridLevel.p * 100) : slPct;
  // When the nearest grid level is one of the always-built 80/90/95 lines,
  // emphasize that line rather than draw a duplicate; otherwise add a dedicated
  // target line from the grid (covers 70/93/98 targets the standard set misses).
  const STANDARD_PCTS = new Set([80, 90, 95]);
  const targetIsStandard = targetPct != null && STANDARD_PCTS.has(targetPct);
  const targetName = targetPct == null ? null : targetIsStandard ? `fcst_q${targetPct}` : 'fcst_target';
  const qTarget = !targetIsStandard && targetGridLevel ? toSparseData(targetGridLevel.values) : null;

  const quantileLegend = quantiles
    ? [
        'fcst_mean',
        ...(targetName ? [targetName] : []),
        ...['fcst_q80', 'fcst_q90', 'fcst_q95'].filter((n) => n !== targetName),
      ]
    : [];

  const showDemand = policy.experiment_id !== POLICY_IDS.keystoneForecast;

  // Anchor the gap indicator to the target line the user is actually shown.
  const gapAnchorData = qTarget ?? (targetName === 'fcst_q80' ? qP80 : targetName === 'fcst_q95' ? qP95 : qP90);
  const lastTargetIdx = gapAnchorData.reduce<number>((found, v, i) => (v != null ? i : found), 0);
  const lastTargetVal = (gapAnchorData[lastTargetIdx] as number | null) ?? anchor;

  const row = (marker: string, name: string, val: string) =>
    `<tr><td style="white-space:nowrap">${marker}${name}</td><td style="padding-left:12px;font-weight:bold;text-align:right">${val}</td></tr>`;

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: CallbackDataParams | CallbackDataParams[]) => {
        const paramsArr: CallbackDataParams[] = Array.isArray(params) ? params : [params];
        const hoveredDate = String(paramsArr[0]?.name ?? '');
        const rows = paramsArr
          .filter((p) => p.seriesName !== '__markers')
          .map((p) => {
            const label = fieldShort(p.seriesName ?? '', 'productStockingLocations');
            return row((p.marker as string) ?? '', label, fmtUnits(typeof p.value === 'number' ? p.value : null));
          });
        if (!rows.length) return '';
        return `<div style="font-size:12px"><b>${hoveredDate}</b><table style="width:100%">${rows.join('')}</table></div>`;
      },
    },
    graphic: [
      { type: 'text', left: 70, top: 6, style: { text: 'TODAY', fill: '#94a3b8', fontWeight: 'bold', fontSize: 11 } },
    ],
    legend: {
      bottom: 0,
      type: 'scroll',
      itemWidth: 24,
      itemHeight: 14,
      itemGap: 18,
      // Data = raw series keys (stable identity for toggling, excludes __markers);
      // displayed via formatter as business labels.
      data: [
        'model_beginning_available',
        'model_beginning_inflight',
        ...(reorder > 0 ? ['model_order_constrained'] : []),
        ...(showDemand ? ['model_expected_demand_over_lt_ph'] : []),
        ...quantileLegend,
      ],
      // Default the recommended-production reorder bar and the non-target
      // service-level lines off; only the target SL line shows by default. The
      // user can toggle the others on to compare.
      selected: {
        model_order_constrained: false,
        fcst_q80: false,
        fcst_q90: false,
        fcst_q95: false,
        ...(targetName ? { [targetName]: true } : {}),
      },
      formatter: (name: string) => {
        if (name === targetName) return `SL ${targetPct}% · Target`;
        const m = /^fcst_q(\d+)$/.exec(name);
        if (m) return `SL ${m[1]}%`;
        return fieldShort(name, 'productStockingLocations');
      },
      textStyle: { fontSize: 13, color: '#334155' },
    },
    grid: { top: 48, right: 56, bottom: 96, left: 64 },
    xAxis: {
      type: 'category',
      data: categories,
      name: 'Week Starting Date',
      nameLocation: 'middle',
      nameGap: 56,
      nameTextStyle: { fontSize: 11, fontWeight: 600, color: '#334155' },
      axisLine: { lineStyle: { color: '#cbd5e1' } },
      axisTick: { show: false },
      // margin clears the ①②③ markers (which sit on the axis) so the dates
      // render below them without overlap.
      axisLabel: { fontSize: 10, color: '#64748b', interval: 0, margin: 26 },
    },
    yAxis: {
      type: 'value',
      name: 'Units (Sale UOM)',
      nameLocation: 'middle',
      nameGap: 48,
      nameTextStyle: { fontSize: 11, fontWeight: 600, color: '#334155' },
      axisLabel: { fontSize: 10, color: '#64748b', formatter: (v: number) => v.toLocaleString() },
      splitLine: { lineStyle: { color: '#f1f5f9' } },
    },
    series: [
      {
        name: 'model_beginning_available',
        type: 'bar',
        stack: 'today',
        barWidth: 48,
        data: [available, ...pad],
        itemStyle: { color: COLOR_AVAILABLE },
      },
      {
        name: 'model_beginning_inflight',
        type: 'bar',
        stack: 'today',
        data: [beginningInflight, ...pad],
        itemStyle: { color: COLOR_INFLIGHT },
      },
      {
        name: 'model_order_constrained',
        type: 'bar',
        stack: 'today',
        data: [reorder, ...pad],
        itemStyle: {
          color: COLOR_REORDER,
          decal: { symbol: 'rect', dashArrayX: [1, 0], dashArrayY: [2, 5], color: 'rgba(255,255,255,0.5)' },
        },
        label: {
          show: reorder > 0,
          position: 'inside',
          formatter: () => `+${Math.round(reorder).toLocaleString()}`,
          color: '#fff',
          fontSize: 11,
          fontWeight: 'bold',
        },
        // Red dashed reference: available-with-reorder level across the window,
        // dropping to the ending inventory position (the gap) at the horizon.
        // The SL 90% gap indicator is also here so toggling this legend item
        // hides both the bar and the indicator together.
        markLine:
          hasGap || (quantiles && reorder > 0)
            ? {
                silent: true,
                symbol: ['none', 'none'],
                lineStyle: { color: COLOR_REORDER, type: 'dashed', width: 1.5 },
                data: [
                  ...(hasGap
                    ? [
                        [{ coord: [0, stackTop] }, { coord: [lastIdx, stackTop] }],
                        [{ coord: [lastIdx, stackTop] }, { coord: [lastIdx, ending] }],
                      ]
                    : []),
                  ...(quantiles
                    ? [
                        [
                          {
                            coord: [0, stackTop],
                            label: {
                              show: true,
                              formatter: reorderGapLabel,
                              position: 'insideMiddleTop',
                              color: COLOR_REORDER,
                              fontSize: 10,
                              fontWeight: 'bold',
                              backgroundColor: '#fff',
                              borderColor: COLOR_REORDER,
                              borderWidth: 1,
                              borderRadius: 4,
                              padding: [3, 8],
                            },
                          },
                          { coord: [lastTargetIdx, stackTop] },
                        ],
                        [
                          { coord: [lastTargetIdx, stackTop] },
                          { coord: [lastTargetIdx, lastTargetVal], symbol: 'arrow', symbolSize: 7 },
                        ],
                      ]
                    : []),
                ],
              }
            : undefined,
      },
      ...(showDemand
        ? [
            {
              name: 'model_expected_demand_over_lt_ph',
              type: 'line',
              smooth: false,
              connectNulls: true,
              symbol: 'circle',
              symbolSize: 5,
              data: demandData,
              itemStyle: { color: COLOR_DEMAND },
              lineStyle: { color: COLOR_DEMAND, width: 2, type: 'dashed' as const },
            },
          ]
        : []),
      {
        name: '__zero_baseline',
        type: 'line',
        data: [],
        silent: true,
        tooltip: { show: false },
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: '#475569', type: 'solid', width: 3 },
          data: [{ yAxis: 0 }],
        },
      },
      ...(quantiles
        ? [
            {
              name: 'fcst_mean',
              type: 'line',
              smooth: false,
              connectNulls: true,
              symbol: 'circle',
              symbolSize: 5,
              data: qMean,
              itemStyle: { color: COLOR_MEAN },
              lineStyle: { color: COLOR_MEAN, width: 3, type: 'solid' as const },
              endLabel: {
                show: true,
                formatter: fieldShort('fcst_mean', 'productStockingLocations'),
                color: COLOR_MEAN,
                fontSize: 11,
                fontWeight: 'bold',
              },
            },
            ...(qTarget
              ? [
                  {
                    name: 'fcst_target',
                    type: 'line' as const,
                    smooth: false,
                    connectNulls: true,
                    symbol: 'circle',
                    symbolSize: 5,
                    data: qTarget,
                    itemStyle: { color: COLOR_TARGET },
                    lineStyle: { color: COLOR_TARGET, width: 4 },
                    endLabel: {
                      show: true,
                      formatter: `SL ${targetPct}%`,
                      color: COLOR_TARGET,
                      fontSize: 11,
                      fontWeight: 'bold',
                    },
                  },
                ]
              : []),
            {
              name: 'fcst_q80',
              type: 'line',
              smooth: false,
              connectNulls: true,
              symbol: 'circle',
              symbolSize: 5,
              data: qP80,
              itemStyle: { color: COLOR_Q80 },
              lineStyle: { color: COLOR_Q80, width: 3 },
              endLabel: {
                show: true,
                formatter: fieldShort('fcst_q80', 'productStockingLocations'),
                color: COLOR_Q80,
                fontSize: 11,
                fontWeight: 'bold',
              },
            },
            {
              name: 'fcst_q90',
              type: 'line',
              smooth: false,
              connectNulls: true,
              symbol: 'circle',
              symbolSize: 5,
              data: qP90,
              itemStyle: { color: COLOR_Q90 },
              lineStyle: { color: COLOR_Q90, width: 4 },
              endLabel: {
                show: true,
                formatter: fieldShort('fcst_q90', 'productStockingLocations'),
                color: COLOR_Q90,
                fontSize: 11,
                fontWeight: 'bold',
              },
            },
            {
              name: 'fcst_q95',
              type: 'line',
              smooth: false,
              connectNulls: true,
              symbol: 'circle',
              symbolSize: 5,
              data: qP95,
              itemStyle: { color: COLOR_Q95 },
              lineStyle: { color: COLOR_Q95, width: 3 },
              endLabel: {
                show: true,
                formatter: fieldShort('fcst_q95', 'productStockingLocations'),
                color: COLOR_Q95,
                fontSize: 11,
                fontWeight: 'bold',
              },
            },
          ]
        : []),
      {
        name: '__markers',
        type: 'scatter',
        data: markerData,
        symbol: 'circle',
        symbolSize: 32,
        itemStyle: { color: '#fff', borderColor: COLOR_INV, borderWidth: 1.5 },
        label: { show: true, color: COLOR_INV, fontSize: 22, fontWeight: 'bold' },
        silent: true,
        tooltip: { show: false },
        z: 5,
      },
    ],
  };
}
